import { useCallback, useState, useMemo, useRef } from 'react';
import type { FC, PropsWithoutRef } from 'react';

import {
  Button,
  Drawer,
  Flex,
  Icon,
  useBreakpoint,
  useComputedLengthPx,
  useDirection,
  useI18n,
  Text,
  useScrollStick,
  useElement,
  useConsolidatedRef
} from '@pega/cosmos-react-core';
import type { ForwardProps } from '@pega/cosmos-react-core';
import type { ScrollStickOptions } from '@pega/cosmos-react-core/lib/hooks/useScrollStick';
import UtilitiesSummary from '@pega/cosmos-react-work/lib/components/CaseView/UtilitiesSummary';
import { StyledCaseDrawerContent } from '@pega/cosmos-react-work';
import { StyledPageIcon } from '@pega/cosmos-react-core/lib/components/PageTemplates/PageTemplates';

import {
  StyledEmailCaseViewColumns,
  StyledExpandedUtilities,
  StyledUtilitiesContainer,
  StyledCollapsedUtilitiesToggle,
  StyledExpandedUtilitiesToggle,
  StyledDrawerUtilitiesToggle,
  StyledEmailCaseViewDrawer,
  StyledEmailCaseViewHeader,
  StyledEmailCaseViewContainer
} from './Email.styles';
import type { EmailCaseViewProps } from './Email.types';

const EmailCaseView: FC<EmailCaseViewProps & ForwardProps> = ({
  header,
  content,
  banners,
  utilities,
  utilitiesSummaryItems,
  defaultUtilitiesExpanded,
  ...restProps
}: PropsWithoutRef<EmailCaseViewProps>) => {
  const direction = useDirection();
  const t = useI18n();

  const [emailCaseEl, setEmailCaseEl] = useElement<HTMLDivElement>();
  const emailCaseRef = useConsolidatedRef(setEmailCaseEl);
  const isMediumOrAbove = useBreakpoint('md', { breakpointRef: emailCaseRef });

  const [showExpandedUtilities, setShowExpandedUtilities] = useState(
    defaultUtilitiesExpanded || false
  );
  const appshellOffsetPx = useComputedLengthPx(emailCaseEl, '--appshell-top-offset');

  // Apply sticky columns
  const scrollStickOptions = useRef<ScrollStickOptions>({
    elements: [null, null]
  });

  const scrollOffset = appshellOffsetPx;

  useScrollStick(
    isMediumOrAbove ? { ...scrollStickOptions.current, offset: scrollOffset } : undefined
  );

  const utilitiesIcon = showExpandedUtilities
    ? `move-${direction.end}-solid`
    : `move-${direction.start}-solid`;

  const onToggleUtilities = useCallback((e: KeyboardEvent | MouseEvent) => {
    if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Enter') {
      return;
    }

    setShowExpandedUtilities(prevState => !prevState);
  }, []);

  const expandedUtilities = useMemo(() => {
    if (!utilities) {
      return null;
    }

    const largeScreenUtility = (
      <>
        {showExpandedUtilities && (
          <Button
            variant='simple'
            icon
            onClick={onToggleUtilities}
            as={StyledExpandedUtilitiesToggle}
            label={t(showExpandedUtilities ? 'collapse_utilities_panel' : 'expand_utilities_panel')}
          >
            <Icon name={utilitiesIcon} />
          </Button>
        )}
        <Flex
          as={StyledExpandedUtilities}
          container={{ direction: 'column', gap: 2 }}
          showExpandedUtilities={showExpandedUtilities}
        >
          {utilities}
        </Flex>
      </>
    );

    const smallScreenUtilities = (
      <Drawer
        as={StyledEmailCaseViewDrawer}
        open={showExpandedUtilities}
        position='fixed'
        placement={direction.end}
        shadow
        nullWhenClosed
      >
        <Flex as={StyledCaseDrawerContent} container={{ direction: 'column', gap: 2 }}>
          <Flex container={{ justify: 'end' }}>
            <Button
              variant='simple'
              icon
              onClick={onToggleUtilities}
              as={StyledDrawerUtilitiesToggle}
              label={t('collapse_utilities_panel')}
            >
              <Icon name={`move-${direction.end}-solid`} />
            </Button>
          </Flex>
          {utilities}
        </Flex>
      </Drawer>
    );

    return isMediumOrAbove ? largeScreenUtility : smallScreenUtilities;
  }, [isMediumOrAbove, onToggleUtilities, showExpandedUtilities, utilities]);

  return (
    <StyledEmailCaseViewContainer {...restProps} ref={emailCaseRef}>
      <Flex container={{ gap: 2 }} as={StyledEmailCaseViewColumns}>
        <Flex item={{ grow: 1, shrink: 1, basis: '0' }}>
          <Flex
            container={{ gap: 1, direction: 'column' }}
            ref={(el: HTMLDivElement | null) => {
              scrollStickOptions.current.elements[0] = el;
            }}
          >
            {header && (
              <Flex
                container={{ alignItems: 'center', pad: [0, 0, 0, 2], gap: 1 }}
                as={StyledEmailCaseViewHeader}
              >
                {header.icon && (
                  <StyledPageIcon container={{ alignItems: 'center', justify: 'center' }}>
                    <Icon name={header.icon} />
                  </StyledPageIcon>
                )}
                <Text variant='h1'>{header.title}</Text>
              </Flex>
            )}
            {banners && <div>{banners}</div>}
            {content && <div>{content}</div>}
          </Flex>
        </Flex>

        {(utilities || utilitiesSummaryItems) && (
          <Flex
            container={{ direction: 'column', alignItems: 'end', gap: 2 }}
            item={{ alignSelf: 'start' }}
            as={StyledUtilitiesContainer}
            showExpandedUtilities={showExpandedUtilities}
            ref={(el: HTMLDivElement) => {
              scrollStickOptions.current.elements[1] = el;
            }}
          >
            {expandedUtilities}
            {!showExpandedUtilities && (
              <Button
                variant='simple'
                icon
                onClick={onToggleUtilities}
                as={StyledCollapsedUtilitiesToggle}
                aria-expanded={false}
                label={t(
                  showExpandedUtilities ? 'collapse_utilities_panel' : 'expand_utilities_panel'
                )}
              >
                <Icon name={utilitiesIcon} />
              </Button>
            )}

            {!showExpandedUtilities &&
              utilitiesSummaryItems &&
              utilitiesSummaryItems.length > 0 && (
                <UtilitiesSummary items={utilitiesSummaryItems} />
              )}
          </Flex>
        )}
      </Flex>
    </StyledEmailCaseViewContainer>
  );
};

export default EmailCaseView;
